import { Money } from "../../../common/models/CommonModels";
import { CategoryReference } from "../../shared/models/customerModels";

export interface ServiceReportsState {
  serviceReportsQuery: ServiceReportsQuery;
  serviceReports?: ServiceReport[];
  serviceReport?: ServiceReport;
  categories: CategoryReference[];
  machineParts: MachinePartGroup[];
  loading: boolean;
  success?: string;
  error: any;
  hasAnyServiceReports?: boolean;
  totalCount: number;
  totalPages: number;
}

export interface ServiceReport {
  id: string;
  /** Machine GUID this SR belongs to — returned by backend VM (MachineId). Used by MIDAS chip resolution (MC-22). */
  machineId?: string;
  number: string;
  title: string;
  affectedPartCategoryId: string;
  description: string;
  notes?: string;
  statusCode: ServiceReportStatusCode;
  cost: Money;
  solvingDescription: string;
  openDate?: Date;
  closeDate?: Date;
  files: ServiceReportFile[];

  // RFS integration
  sourceType?: ServiceReportSourceType;
  serviceTypeCode?: number;
  serviceRequestInternalNumber?: string;
  providerCompanyName?: string;
  actualLaborHours?: number;
  actualPartsCost?: number;

  // Creator tracking
  createdByUserId?: string;
  createdByName?: string;
  createdByRole?: string;
  solvedByName?: string;
  solvedByRole?: string;
  modifiedByName?: string;

  // Alarm / Urgency
  alarmCode?: string;
  urgency?: number;
}

export enum ServiceReportSourceType {
  manual = 1,
  requestForService = 2,
  maintenancePlan = 3,
}

export interface ServiceReportFile {
  name: string;
  content: string;
  size: number;
  typeCode?: ServiceReportFileTypeCode;
}

export interface ChangeServiceReport {
  title: string;
  affectedPartCategoryId?: string;
  description: string;
  notes?: string;
  alarmCode?: string;
  urgency?: number;
  createdViaMidas?: boolean;
  openDate?: Date;
  files: ServiceReportFile[] | null;
}

export interface ChangeSolvingServiceReport {
  cost?: Money;
  solvingDescription: string;
  closeDate?: Date;
  files: ServiceReportFile[] | null;
}

export interface ServiceReportsQuery {
  search?: string;
  statusCodes?: ServiceReportStatusCode[];
  openDateFrom?: Date;
  openDateTo?: Date;
  sortBy?: ServiceReportsQueryParameter;
  sortDesc?: boolean;
  pageNo?: number;
  pageSize?: number;
}

export enum ServiceReportsQueryParameter {
  Title = 1,
  Number = 2,
  OpenDate = 3,
  CloseDate = 4,
  StatusCode = 5,
  CostValue = 6
}

export interface ServiceReportsPage {
  items: ServiceReport[];
  pageNo: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export enum ServiceReportStatusCode {
  inProgress = 1,
  solved = 2
}

export enum ServiceReportFileTypeCode {
  reportDocument = 1,
  solvingReportDocument = 2
}

class ServiceReportCodes {
  private readonly map = {} as any;

  constructor() {
    this.map[ServiceReportStatusCode.inProgress] = "In Progress";
    this.map[ServiceReportStatusCode.solved] = "Solved";
  }

  serviceReportCode = (code: ServiceReportStatusCode) => {
    return this.map[code] || '';
  };
}

export const statusCodeToTranslationKey = {
  [ServiceReportStatusCode.inProgress]: "serviceReportStatus.inProgress",
  [ServiceReportStatusCode.solved]: "serviceReportStatus.solved"
};

export const { serviceReportCode } = new ServiceReportCodes();

// Machine Parts (from Machinery API /v1/machine-parts)
export interface MachinePartItem {
  id: string;
  name: string;
  groupKey: string;
  code: string;
  sortOrder: number;
}

export interface MachinePartGroup {
  groupKey: string;
  parts: MachinePartItem[];
}